import { CTA } from "@/components/CTA";
import { Reveal } from "@/components/Reveal";
import { PageHeader, Kicker } from "@/components/Section";
import { Marquee } from "@/components/Marquee";
import { NewsletterInline } from "@/components/Footer";
import { Star, Heart, Crown } from "@/components/Doodles";
import { ARCHITECT } from "@/data/characters";
import { useOrgSettings } from "@/lib/org";

export default function About() {
  const org = useOrgSettings();
  const values = [
    { icon: Star, t: "Collaborative", b: "No creative works alone. Every Issue is made by a crew across disciplines." },
    { icon: Heart, t: "Youthful", b: "We're built by and for emerging creatives — scrappy, bold, and a little rebellious." },
    { icon: Crown, t: "Collectible", b: "We treat campaigns like comic Issues: numbered, credited, and worth keeping." },
  ];
  return (
    <div className="bg-onyx text-cream">
      <PageHeader
        kicker="About"
        titleLines={["We are little.", "Our ideas", "are big."]}
        subtitle="Big Little Village is a nonprofit creative movement giving emerging creatives the real experience, collaboration, and portfolios that open doors."
        dark
      />

      <section className="grain-dark border-b border-gold/20 bg-charcoal py-20 sm:py-28">
        <div className="relative mx-auto max-w-[900px] px-4 sm:px-6">
          <Reveal><Kicker>The Vision</Kicker></Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 font-head text-2xl font-bold leading-snug text-cream sm:text-3xl">
              We're building the headquarters of a youth creative movement — not a traditional nonprofit brochure.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 font-body text-lg leading-relaxed text-silver/85">
              Experience shouldn't be gatekept behind unpaid internships and closed networks. Big Little Village
              organizes emerging creatives into multidisciplinary cohorts and hands them real creative briefs.
              The result: real collaboration, real process, real execution — and a portfolio that proves it.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-4 font-body text-lg leading-relaxed text-silver/85">
              We're honest about outcomes. Completing a cohort does not guarantee employment. It opens paid
              creative opportunities, freelance opportunities, agency opportunities, and employment pathways —
              and it clearly distinguishes portfolio-based founding projects from paid, sponsored work.
            </p>
          </Reveal>
        </div>
      </section>

      <Marquee items={["Youthful", "Collectible", "Collaborative", "Slightly Rebellious"]} />

      <section className="grain-dark relative bg-onyx py-20 sm:py-28">
        <div className="halftone-gold pointer-events-none absolute inset-0 opacity-[0.03]" />
        <div className="relative mx-auto max-w-[1400px] px-4 sm:px-6">
          <Reveal><Kicker>What We Value</Kicker></Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {values.map((v, i) => (
              <Reveal key={v.t} delay={i * 0.08}>
                <div className="group h-full border border-silver/20 bg-charcoal/60 p-8 transition-all duration-300 hover:border-gold/50 hover:shadow-[0_0_30px_rgba(201,162,39,0.12)]">
                  <v.icon className="h-12 w-12" />
                  <h3 className="mt-5 font-display text-3xl uppercase tracking-tight text-cream transition-colors group-hover:text-goldlight">{v.t}</h3>
                  <p className="mt-2 font-body leading-relaxed text-silver/75">{v.b}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="grain-dark relative overflow-hidden border-y border-gold/25 bg-charcoal py-20 sm:py-28" data-testid="architect-section">
        <div className="halftone-gold pointer-events-none absolute inset-0 opacity-[0.04]" />
        <div className="relative mx-auto grid max-w-[1200px] items-center gap-12 px-4 sm:px-6 lg:grid-cols-[0.8fr_1.2fr]">
          <Reveal>
            <div className="group panel-mono relative mx-auto max-w-sm gold-frame overflow-hidden">
              <img src={ARCHITECT.img} alt="The Architect — an intentionally obscured figure" className="w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-onyx/70 to-transparent" />
              <span className="absolute bottom-3 left-3 -rotate-2 font-marker text-xl text-goldlight">who tho?? ✦</span>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <Kicker>{ARCHITECT.code}</Kicker>
            <h2 className="mt-5 font-display text-5xl uppercase leading-[0.85] tracking-tight text-metallic sm:text-7xl">The Architect</h2>
            <dl className="mt-6 space-y-1 font-mono text-sm text-silver">
              <div className="flex gap-3"><dt className="w-24 text-silver/50">IDENTITY:</dt><dd className="font-bold tracking-widest text-goldlight">{ARCHITECT.identity}</dd></div>
              <div className="flex gap-3"><dt className="w-24 text-silver/50">ROLE:</dt><dd className="font-bold">{ARCHITECT.role}</dd></div>
            </dl>
            <blockquote className="mt-8 border-l-2 border-gold bg-onyx/70 p-6">
              <p className="font-body text-lg italic leading-relaxed text-silver/90">
                "Big Little Village began with a simple question: What happens when emerging creatives are given
                something real to build together? You don't need to know who built the door. Just know it's open."
              </p>
              <cite className="mt-3 block font-marker text-lg not-italic text-goldlight">✦ The Architect</cite>
            </blockquote>
          </Reveal>
        </div>
      </section>

      <section className="grain-dark relative bg-onyx py-20 sm:py-28">
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[70vmin] w-[70vmin] -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ background: "radial-gradient(circle, rgba(201,162,39,0.1) 0%, transparent 60%)" }} />
        <div className="relative mx-auto max-w-[900px] px-4 text-center sm:px-6">
          <h2 className="font-display text-5xl uppercase leading-[0.85] tracking-tight text-metallic sm:text-6xl">
            Be part of the movement.
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <CTA to="/apply" variant="pink">Apply Now</CTA>
            <CTA to="/campaigns" variant="ink">See the Work</CTA>
          </div>
          <div className="mt-12 flex flex-col items-center gap-3">
            <p className="font-cinzel text-[10px] uppercase tracking-[0.35em] text-gold">Get the newsletter</p>
            <NewsletterInline />
          </div>
          <div className="mx-auto mt-14 max-w-sm border-t border-gold/20 pt-8 text-center" data-testid="about-org-credentials">
            <p className="font-cinzel text-[10px] uppercase tracking-[0.35em] text-gold">{org.org_name}</p>
            <div className="mt-3 space-y-1 font-mono text-[10px] uppercase tracking-[0.2em] text-silver/60">
              <p>{org.nonprofit_designation}</p>
              {org.ein && <p>EIN: {org.ein}</p>}
              <p>Founding Cohorts: {org.founding_location}</p>
              <p>
                <a href={`mailto:${org.email_general}`} data-testid="about-email-general" className="text-goldlight/80 transition-colors hover:text-goldlight">{org.email_general}</a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
